/**
 * GameNotification - Slide-in notification for energy and inventory changes
 * Shows a popup from the right side below the header when player state changes
 */

import { useEffect } from 'react';
import { X, Zap, Package } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface GameNotificationProps {
  type: 'energy' | 'inventory';
  message: string;
  change?: number;           // For energy: +/- amount
  itemName?: string;         // For inventory: item that changed
  isVisible: boolean;
  onDismiss: () => void;
}

export function GameNotification({
  type,
  message,
  change,
  itemName,
  isVisible,
  onDismiss,
}: GameNotificationProps) {
  // Auto-dismiss after 5 seconds
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        onDismiss();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isVisible, onDismiss]);

  // Determine colors based on type and change direction
  const isPositive = type === 'inventory' || (type === 'energy' && change !== undefined && change > 0);
  const isNegative = type === 'energy' && change !== undefined && change < 0;

  const bgColor = isPositive
    ? 'bg-[#367A25]/10 border-[#367A25]/30'
    : isNegative
    ? 'bg-[#DD6832]/10 border-[#DD6832]/30'
    : 'bg-[#12356B]/10 border-[#12356B]/30';

  const iconBgColor = isPositive
    ? 'bg-[#367A25]/15 text-[#367A25]'
    : isNegative
    ? 'bg-[#DD6832]/15 text-[#DD6832]'
    : 'bg-[#12356B]/15 text-[#12356B]';

  const textColor = isPositive
    ? 'text-[#367A25]'
    : isNegative
    ? 'text-[#DD6832]'
    : 'text-[#12356B]';

  const changeColor = isPositive
    ? 'text-[#367A25]'
    : 'text-[#DD6832]';

  return (
    <div
      className={cn(
        'fixed top-20 right-4 z-50 transform transition-all duration-300 ease-out',
        isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
      )}
    >
      <div
        className={cn(
          'flex items-center gap-3 px-4 py-3 rounded-lg border shadow-lg min-w-[280px] max-w-[360px] backdrop-blur-sm',
          bgColor
        )}
      >
        {/* Icon */}
        <div className={cn('size-10 rounded-full flex items-center justify-center flex-shrink-0', iconBgColor)}>
          {type === 'energy' ? (
            <Zap className="size-5" />
          ) : (
            <Package className="size-5" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className={cn('font-medium text-sm', textColor)}>{message}</p>
          {type === 'energy' && change !== undefined && (
            <p className={cn('text-lg font-bold tabular-nums', changeColor)}>
              {change > 0 ? '+' : ''}{change} Energy
            </p>
          )}
          {type === 'inventory' && itemName && (
            <p className={cn('text-sm', textColor)}>
              {itemName}
            </p>
          )}
        </div>

        {/* Dismiss button */}
        <button
          onClick={onDismiss}
          className={cn(
            'size-6 rounded-full flex items-center justify-center flex-shrink-0 hover:bg-black/10 transition-colors',
            textColor
          )}
          aria-label="Dismiss notification"
        >
          <X className="size-4" />
        </button>
      </div>
    </div>
  );
}
